// src/components/RoutePerformance.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase';
import currencyService from '../services/currencyService';
import './RoutePerformance.css';

export default function RoutePerformance() {
  const { routeId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [routeData, setRouteData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState({
    purchases: 0,
    revenueUSD: 0,
    revenueDisplay: 0,
    currency: 'USD'
  });
  const [error, setError] = useState('');

  const loadRouteData = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        navigate('/');
        return;
      }

      // Load route data
      const routeDoc = await getDoc(doc(db, 'routes', routeId));

      if (!routeDoc.exists()) {
        setError('Route not found');
        setLoading(false);
        return;
      }

      const data = routeDoc.data();

      // Verify user owns this route
      if (data.creatorId !== user.uid) {
        setError('You do not have permission to view this route');
        setLoading(false);
        return;
      }

      setRouteData({ id: routeDoc.id, ...data });

      // Get user's preferred currency
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const preferredCurrency = userDoc.exists() ? userDoc.data().preferredCurrency || 'USD' : 'USD';

      // Query journeys collection for purchases of this route
      const journeysQuery = query(
        collection(db, 'journeys'),
        where('routeIds', 'array-contains', routeId)
      );

      const journeysSnapshot = await getDocs(journeysQuery);

      // Calculate purchases and creator earnings
      const purchases = journeysSnapshot.size;

      // Creator earnings: $25 USD per purchase
      const earningsUSD = purchases * 25;

      // Convert earnings to user's preferred currency
      let earningsDisplay = earningsUSD;
      let displayCurrency = 'USD';

      if (preferredCurrency !== 'USD') {
        try {
          const convertedEarnings = await currencyService.convertFromUSD(earningsUSD, preferredCurrency);
          earningsDisplay = convertedEarnings.amount;
          displayCurrency = preferredCurrency;
        } catch (conversionError) {
          console.error(`Error converting to ${preferredCurrency}:`, conversionError);
          // Fallback to USD
          earningsDisplay = earningsUSD;
          displayCurrency = 'USD';
        }
      }

      setAnalyticsData({
        purchases,
        revenueUSD: earningsUSD,
        revenueDisplay: earningsDisplay,
        currency: displayCurrency
      });

    } catch (err) {
      console.error('Error loading route data:', err);
      setError('Failed to load route data');
    } finally {
      setLoading(false);
    }
  }, [routeId, navigate]);

  useEffect(() => {
    loadRouteData();
  }, [loadRouteData]);

  if (loading) {
    return (
      <div className="performance-loading">
        <div className="loading-spinner">⏳</div>
        <p>Loading performance data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="performance-container">
        <div className="performance-content">
          <div className="error-state">
            <h2>Error</h2>
            <p>{error}</p>
            <button className="btn-back" onClick={() => navigate('/creator-dashboard')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="performance-container">
      <div className="performance-content">
        {/* Header */}
        <div className="performance-header">
          <button className="btn-back" onClick={() => navigate('/creator-dashboard')}>
            ← Back to Dashboard
          </button>
          <h1 className="performance-title">
            {routeData?.name || routeData?.routeName || 'Route'} Performance
          </h1>
        </div>

        {/* Performance Stats */}
        <div className="stats-grid">
          <div className="performance-stat-card">
            <div className="stat-icon">🛒</div>
            <div className="stat-value">{analyticsData.purchases}</div>
            <div className="stat-label">Purchases</div>
          </div>

          <div className="performance-stat-card">
            <div className="stat-icon">💰</div>
            <div className="stat-value">
              {currencyService.formatCurrency(analyticsData.revenueDisplay, analyticsData.currency)}
            </div>
            <div className="stat-label">Creator Earnings</div>
          </div>
        </div>

        {/* Route Info */}
        <div className="info-section">
          <h2 className="section-title">Route Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Status:</span>
              <span className="info-value">{routeData?.creatorStatus || 'draft'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Store Status:</span>
              <span className="info-value">{routeData?.storeStatus || 'Not Live'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Created:</span>
              <span className="info-value">
                {routeData?.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Last Updated:</span>
              <span className="info-value">
                {routeData?.updatedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Coming Soon */}
        <div className="coming-soon-section">
          <div className="coming-soon-card">
            <h3>📈 More Analytics Coming Soon</h3>
            <p>We're working on bringing you more detailed analytics including:</p>
            <ul>
              <li>Total page views and view tracking</li>
              <li>Conversion rate (purchases/views)</li>
              <li>Daily purchase trends and revenue over time</li>
              <li>Geographic breakdown of your customers</li>
              <li>Customer demographics and travel patterns</li>
              <li>Performance comparison with other routes</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
